import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Clock, ShieldAlert, LogOut } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import { api } from '../../api/client';

const INACTIVITY_WARNING_MS = 25 * 60 * 1000; // 25 minutes
const INACTIVITY_LOGOUT_MS = 30 * 60 * 1000;  // 30 minutes

export default function SessionTimeoutHandler() {
  const { user, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds

  const lastActivityRef = useRef(Date.now());
  const lastPingRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showWarning) {
      setShowWarning(false);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    }
    // Ping backend to refresh PHP session activity if > 3 minutes elapsed
    if (Date.now() - lastPingRef.current > 3 * 60 * 1000) {
      lastPingRef.current = Date.now();
      api.get('/auth/ping').catch(() => {});
    }
  }, [showWarning]);

  const handleInactivityLogout = useCallback(async () => {
    setShowWarning(false);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    const role = user?.role;
    await logout();
    
    toast.warning('You have been signed out due to 30 minutes of inactivity.', 'Session Expired');
    
    if (['admin', 'super_admin', 'staff'].includes(role)) {
      navigate('/admin/login');
    } else if (role === 'technician') {
      navigate('/technician/login');
    } else {
      navigate('/customer/login');
    }
  }, [user, logout, toast, navigate]);

  // Track window activity
  useEffect(() => {
    if (!user) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleEvent = () => resetActivity();

    events.forEach((evt) => window.addEventListener(evt, handleEvent, { passive: true }));

    // Periodic check every 15 seconds
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;

      if (elapsed >= INACTIVITY_LOGOUT_MS) {
        handleInactivityLogout();
      } else if (elapsed >= INACTIVITY_WARNING_MS && !showWarning) {
        setShowWarning(true);
        setCountdown(Math.max(0, Math.floor((INACTIVITY_LOGOUT_MS - elapsed) / 1000)));

        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              handleInactivityLogout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }, 15000);

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handleEvent));
      clearInterval(interval);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [user, resetActivity, showWarning, handleInactivityLogout]);

  // Listen to 401 unauthorized events from Axios interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      if (user) {
        logout();
        toast.error('Your session has expired. Please sign in again.', 'Authentication Required');
      }
    };

    window.addEventListener('auth:session_expired', handleAuthExpired);
    return () => window.removeEventListener('auth:session_expired', handleAuthExpired);
  }, [user, logout, toast]);

  if (!showWarning || !user) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const formattedTime = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

  return (
    <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-amber-200 text-center space-y-4">
        <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto">
          <Clock className="w-6 h-6 animate-pulse" />
        </div>

        <div>
          <h3 className="font-bold text-base text-gray-900">Session Inactivity Notice</h3>
          <p className="text-xs text-gray-600 mt-1">
            You have been inactive. For your security, your session will automatically terminate in:
          </p>
          <div className="text-2xl font-black text-amber-600 font-mono my-2 tracking-wider">
            {formattedTime}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={handleInactivityLogout}
            className="flex-1 py-2.5 px-3 rounded-lg border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-50 flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Sign Out</span>
          </button>

          <button
            type="button"
            onClick={resetActivity}
            className="flex-1 py-2.5 px-3 rounded-lg bg-[#00796b] text-white text-xs font-bold hover:bg-[#00796b] shadow-xs cursor-pointer active:scale-95 transition-all"
          >
            Stay Signed In
          </button>
        </div>
      </div>
    </div>
  );
}
